import { useEffect, useState } from 'react';

import { Redirect, Route, Switch, useHistory } from 'react-router-dom';

import { Alert, Box, CloseButton, Paragraph, Tabs } from '@zillow/constellation-10';

import { Col, Container, Row } from '@zg-rentals/zrs-comet';

// // Components
import PaymentInfoModal from '../../components/modals/PaymentInfoModal';

// // containers
import PageWrapper from '../../components/PageWrapper';
import PaymentDuePage from '../PaymentDuePage';
import PaymentSettingsPage from '../PaymentSettingsPage';
import PaidInvoicesPage from '../PaidInvoicesPage';
import BillingPortalMaintenanceBanner from '../../components/BillingPortalMaintenanceBanner';

import billingUtils from '../../utils/billingUtils';

import { withErrorBoundary } from '../../components/withErrorBoundry';
import {
  AuthCheckWrapper,
  HeadWrapper,
  useRentalManagerUser,
  useRmwPaths,
  useRouter,
} from '@zg-rentals/rental-manager-env';
import type { History } from 'history';
import InvoiceSearchBox from '../../components/InvoiceSearchBox';
import { useAppBanner } from '../../hooks/useBanner';
import { css } from '@/styled-system/css';

const configContent = {
  backgroundColor: 'white',
  borderRadius: '5px',
  borderColor: 'gray.300',
};

const DEFAULT_PAGE_TITLE = 'Billing';

const TabList = {
  paid: 'paid',
  due: 'due',
  paymentSettings: 'paymentSettings',
};

const BillingPage = () => {
  const paths = useRmwPaths();
  const { user } = useRentalManagerUser();
  const router = useRouter();
  const { bannerState, setBannerState } = useAppBanner();
  const { displaySuccessMessage } = bannerState;
  const { canAccessBilling } = user.features;
  const isOnlyNucleusUser = billingUtils.isOnlyNucleusUser(user);
  const isSearchEnabled = !billingUtils.isOnlyNucleusUser(user);
  const [showPaymentInfoModal, setShowPaymentInfoModal] = useState(false);
  const history = useHistory<History>();

  useEffect(() => {
    return () => {
      setBannerState({ displaySuccessMessage: false });
    };
  }, []);

  const loadTab = (tab: string) => {
    const url = `${paths.billingPage}/${tab}`;
    history.push(url);
  };

  const getActiveTab = () => {
    // Expecting path to be [baseUrl]/rental-manager/billing/*something*
    const tabName = router.location.pathname.split('/')[3];

    switch (tabName) {
      case paths.billingDue:
        return paths.billingDue;
      case paths.billingPaid:
        return paths.billingPaid;
      case 'paymentSettings':
        return 'paymentSettings';
      default:
        return paths.billingDue;
    }
  };

  const getSectionUrl = (section: string) => {
    const baseDetailsUrl = paths.billingPage;
    return `${baseDetailsUrl}/${section}([?].*)?`;
  };

  const renderChildRoutes = () => {
    const baseDetailsUrl = paths.billingPage;
    const childPathDue = getSectionUrl('due');
    const childPathPaid = getSectionUrl('paid');
    const childPathPaymentSettings = getSectionUrl('paymentSettings');
    return (
      <Switch>
        <Route
          render={() => {
            if (isOnlyNucleusUser) {
              return <Redirect to={`${baseDetailsUrl}/${paths.billingPaid}`} />;
            }
            return <Route component={PaymentDuePage} path={childPathDue} />;
          }}
          path={childPathDue}
        />
        <Route component={PaidInvoicesPage} path={childPathPaid} />
        <Route component={PaymentSettingsPage} path={childPathPaymentSettings} />
      </Switch>
    );
  };

  const renderSubNav = () => (
    <Box
      css={{
        paddingRight: 'lg',
        paddingLeft: 'lg',
        marginTop: 'xs',
        marginBottom: 'xs',
      }}
    >
      <Tabs.Root defaultSelected={getActiveTab()} onSelectedChange={loadTab} aria-label="billing-portal-tabs">
        <Tabs.List>
          <Tabs.Tab value={TabList.due}>Payment Due</Tabs.Tab>
          <Tabs.Tab value={TabList.paid}>Payment history</Tabs.Tab>
          <Tabs.Tab value={TabList.paymentSettings}>Payment settings</Tabs.Tab>
        </Tabs.List>
      </Tabs.Root>
    </Box>
  );

  const renderWidget = () => (isSearchEnabled ? <InvoiceSearchBox /> : undefined);

  const renderContent = () => {
    if (!canAccessBilling) {
      return (
        <PageWrapper
          title={DEFAULT_PAGE_TITLE}
          config={{
            fluid: false,
          }}
          configContent={configContent}
        >
          <Box css={{ padding: 'lg' }}>
            <Paragraph>
              Looking for invoices? If you manage multiple billing accounts, please log out of this account and try a
              different one.
            </Paragraph>
          </Box>
        </PageWrapper>
      );
    }
    return (
      <PageWrapper
        title={DEFAULT_PAGE_TITLE}
        widget={renderWidget()}
        config={{
          fluid: false,
        }}
        configContent={configContent}
        alert={<BillingPortalMaintenanceBanner />}
      >
        <Container>
          {displaySuccessMessage && (
            <Row hidden={[]}>
              <Col xs={12}>
                <Alert
                  isOpen={true}
                  tone="success"
                  onClose={() => setBannerState({ displaySuccessMessage: false })}
                  closeButton={<CloseButton />}
                >
                  Thank you for your payment!
                </Alert>
              </Col>
            </Row>
          )}
          <Container className={css({ marginBottom: '32px' })}>
            {!isOnlyNucleusUser && renderSubNav()}
            {renderChildRoutes()}
          </Container>
        </Container>
      </PageWrapper>
    );
  };

  return (
    <AuthCheckWrapper>
      <div className="BillingPage">
        <HeadWrapper title={DEFAULT_PAGE_TITLE} />
        {renderContent()}
        {showPaymentInfoModal && <PaymentInfoModal onClose={() => setShowPaymentInfoModal(false)} />}
      </div>
    </AuthCheckWrapper>
  );
};

export default withErrorBoundary(BillingPage);
